import {type ComponentType} from 'react';
import type {AlertItem, AlertType, ModalItem, PopupData, PopupItem, PopupType} from '../compoents/modal';
import {useCallback} from 'react';
import useGlobalStore from '../store/useGlobalStore.ts';

const useModal = () => {
    const {push, pop, remove, clear} = useGlobalStore();

    const showPopup = useCallback(
        (
            popupType: PopupType,
            data: PopupData,
            options?: {
                onClose?: () => void;
                onConfirm?: () => void;
            }
        ) => {
            const popupItem: Omit<PopupItem, 'zIndex'> = {
                id: data.id,
                type: 'popup',
                popupType,
                data,
                onClose: options?.onClose,
                onConfirm: options?.onConfirm,
            };
            return push(popupItem);
        },
        [push]
    );

    const showAlert = useCallback(
        (
            id: string,
            alertType: AlertType,
            title: string,
            message: string,
            options?: {
                onClose?: () => void;
                onConfirm?: () => void;
            }
        ) => {
            const alertItem: Omit<AlertItem, 'zIndex'> = {
                id: id,
                type: 'alert',
                alertType,
                title,
                message,
                onClose: options?.onClose,
                onConfirm: options?.onConfirm,
            };
            return push(alertItem);
        },
        [push]
    );

    const showModal = useCallback(
        (
            id: string,
            component: ComponentType<any>,
            props?: any,
            options?: {
                onClose?: () => void;
            }
        ) => {
            const modalItem: Omit<ModalItem, 'zIndex'> = {
                id: id,
                type: 'modal',
                component,
                props,
                onClose: options?.onClose,
            };
            return push(modalItem);
        },
        [push]
    );

    const close = useCallback(
        (id?: string) => {
            if (id) {
                remove(id);
            } else {
                pop();
            }
        },
        [remove, pop]
    );

    const closeAll = useCallback(() => {
        clear();
    }, [clear]);

    return {
        showPopup,
        showAlert,
        showModal,
        close,
        closeAll,
    };
};

export default useModal;
